/**
 * TERRAIN DECOUPLE round (docs/bim-plan/19) — the captured-terrain-profile READER,
 * shared by every consumer (foundation layout, finish skirt, the survey diff).
 * Pure math over the stored TerrainFit lanes; no TIN, no clipper — this is the
 * whole point: a captured wall generates without ever touching the terrain.
 */
import type { TerrainFit } from './types'

/** shape-guard a raw param value into a usable TerrainFit, else null */
export function terrainFitOf(v: unknown): TerrainFit | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null
  const f = v as Partial<TerrainFit>
  if (f.v !== 1) return null
  if (!Array.isArray(f.stations) || !Array.isArray(f.center) || !Array.isArray(f.high)) return null
  if (f.stations.length < 2) return null
  if (f.center.length !== f.stations.length || f.high.length !== f.stations.length) return null
  if (!Array.isArray(f.skirtStations) || !Array.isArray(f.skirt)) return null
  if (f.skirt.length !== f.skirtStations.length) return null
  return f as TerrainFit
}

/**
 * Read one lane at a station FRACTION (0..1, clamped). Linear interpolation
 * between bracketing samples; when exactly one neighbor is null (the terrain
 * hull edge), the nearer sample wins — mirroring the live sampler's per-point
 * null semantics on a dense (≈2ft) grid; both null = null (no terrain there,
 * the manual-height fallback applies).
 */
export function terrainFitLane(
  stations: number[], lane: (number | null)[], frac: number,
): number | null {
  const n = stations.length
  if (!n || lane.length !== n) return null
  const f = Math.max(stations[0], Math.min(stations[n - 1], frac))
  // binary search for the bracketing pair
  let lo = 0, hi = n - 1
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1
    if (stations[mid] <= f) lo = mid
    else hi = mid
  }
  const a = lane[lo], b = lane[hi]
  const s0 = stations[lo], s1 = stations[hi]
  if (a === null && b === null) return null
  // one-null neighbor (the terrain hull edge): the NEARER sample wins, null
  // included — terrain must not bleed half a cell into a no-terrain zone
  if (a === null || b === null) return f - s0 <= s1 - f ? a : b
  if (s1 - s0 < 1e-12) return Math.max(a, b)
  const t = (f - s0) / (s1 - s0)
  return a + (b - a) * t
}
